import { IconSet } from '@postscript/components';
import {
  BCC,
  ENTITLEMENTS_BRAND_CENTER,
  PRODUCT_CATALOG,
} from 'components/admin/utils/feature-flags';
import { useInfinityTestingBillingStatus } from 'components/billing/modules/plans/hooks/useInfinityTestingBillingStatus';
import { useShopperBillingStatus } from 'components/billing/modules/plans/hooks/useShopperBillingStatus';
import { BrandCenterRoutes } from 'components/brand/types';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useShopperStatus } from 'hooks/useShopperStatus';
import { useMemo } from 'react';
import { NavMenuItem, SubMenuItem } from '../NavMenu';

const getBrandCenterLink = (url: string) =>
  url.includes('/sources')
    ? BrandCenterRoutes.SOURCES
    : BrandCenterRoutes.VOICE;

export const useBrandCenterMenuItems = () => {
  const shopperStatusContext = useShopperStatus();
  const {
    isEligible: isEligibleForInfinityTesting,
    hasProduct: hasInfinityTestingProduct,
  } = useInfinityTestingBillingStatus();
  const { isEligible: isEligibleForShopper } = useShopperBillingStatus();
  const { hasLabsFlag } = usePSLabs();
  const hasBccEnabled = hasLabsFlag(BCC);
  const hasProductCatalogEnabled = hasLabsFlag(PRODUCT_CATALOG);
  const hasEntitlementsFlag = hasLabsFlag(ENTITLEMENTS_BRAND_CENTER);

  const showBrandCenterMenuItem =
    hasBccEnabled ||
    isEligibleForInfinityTesting ||
    hasInfinityTestingProduct ||
    isEligibleForShopper ||
    shopperStatusContext.hasShopperFeatureEnabled;

  const highlightShopper =
    isEligibleForShopper || shopperStatusContext.hasShopperFeatureEnabled;

  return useMemo(
    (): NavMenuItem[] =>
      showBrandCenterMenuItem
        ? [
            {
              id: 'brand-center',
              label: 'Brand Center',
              icon: IconSet.SettingsSliders,
              isLoading: shopperStatusContext.isLoading,
              ariaLabelledBy: 'nav-brand-center-title',
              ...(highlightShopper ? { tag: { content: 'New' } } : {}),
              subMenu: [
                {
                  link: () => getBrandCenterLink(window.location.pathname),
                  label: 'Voice and Tone',
                  id: 'voice-and-tone',
                  entitlementKey: hasEntitlementsFlag
                    ? 'ai::brand_center::view'
                    : undefined,
                },
                ...((shopperStatusContext.hasShopperFeatureEnabled ||
                isEligibleForShopper
                  ? [
                      {
                        link: () => BrandCenterRoutes.SHOPPER,
                        label: 'Shopper',
                        id: 'shopper',
                        tag: { content: 'Beta', variant: 'info' as const },
                        entitlementKey: hasEntitlementsFlag
                          ? 'ai::shopper::eligible'
                          : undefined,
                      },
                    ]
                  : []) as SubMenuItem[]),
                ...((hasProductCatalogEnabled
                  ? [
                      {
                        link: BrandCenterRoutes.PRODUCTS,
                        label: 'Product Catalog',
                        id: 'product-catalog',
                      },
                    ]
                  : []) as SubMenuItem[]),
              ],
            },
          ]
        : [],
    [
      showBrandCenterMenuItem,
      shopperStatusContext.isLoading,
      shopperStatusContext.hasShopperFeatureEnabled,
      highlightShopper,
      hasEntitlementsFlag,
      isEligibleForShopper,
      hasProductCatalogEnabled,
    ],
  );
};
